<template>
  <div>
    <div>
      <h4 style="display: inline-block; text-transform: uppercase; margin-right: 20px">{{ gameMetadata.GameName }}</h4>
      <h5 style="display: inline-block; text-transform: uppercase">
        {{ gameMetadata.Location }},
        {{ gameMetadata.GameDateOverride || gameMetadata.GameDate }}
      </h5>
    </div>

    <div class="asymmetric-input-group d-flex flex-v-centre">
      <div class="input-holder wide has-dropdown">
        <label for="HospitalityTicketInventoryId">Hospitality &amp; Tickets*</label>
        <div class="select-holder">
          <Select id="HospitalityTicketInventoryId" name="HospitalityTicketInventoryId" v-model="model.HospitalityTicketInventoryId"
            option-value="InventoryId" :option-label="formatHospitalityTicketInventory" :option-disabled="item => item.IsSoldOut"
            :options="gameMetadata.HospitalityTicketInventory" placeholder="Hospitality &amp; Ticket options" show-clear append-to="self"> </Select>
        </div>
        <small class="p-error" v-if="form.HospitalityTicketInventoryId.$error">{{ form.HospitalityTicketInventoryId.$errors[0].$message }}</small>
      </div>
      <div class="input-holder">
        <label for="NumberOfPeople">Number of People*</label>
        <input type="number" id="NumberOfPeople" name="NumberOfPeople" v-model="model.NumberOfPeople" min="1" max="500"
          :disabled="!!hospitalityTicketInventory?.ForcedQuantity" />
        <small class="p-error" v-if="form.NumberOfPeople.$error">{{ form.NumberOfPeople.$errors[0].$message }}</small>
      </div>
    </div>
    <NotEnoughStockWarning v-if="form.HospitalityTicketInventoryId.stock.$invalid && model.NumberOfPeople > 0" />

    <div class="asymmetric-input-group d-flex flex-v-centre" v-if="!!gameMetadata.AccommodationInventory.length">
      <div class="input-holder wide has-dropdown">
        <label for="AccommodationInventoryId">Accommodation<span v-if="isAccommodationRequired">*</span></label>
        <div class="select-holder">
          <Select id="AccommodationInventoryId" name="AccommodationInventoryId" v-model="model.AccommodationInventoryId" option-value="InventoryId"
            :option-label="formatAccommodation" :option-disabled="item => item.IsSoldOut" :options="gameMetadata.AccommodationInventory"
            placeholder="Accommodation options" show-clear append-to="self"> </Select>
        </div>
        <small class="p-error" v-if="form.AccommodationInventoryId.$error">{{ form.AccommodationInventoryId.$errors[0].$message }}</small>
      </div>
      <div class="input-holder">
        <label for="NumberOfRooms">How many rooms?<span v-if="isAccommodationRequired">*</span></label>
        <input type="number" id="NumberOfRooms" name="NumberOfRooms" v-model="model.NumberOfRooms" min="0" :max="model.NumberOfPeople" />
        <small class="p-error" v-if="form.NumberOfRooms.$error">{{ form.NumberOfRooms.$errors[0].$message }}</small>
      </div>
    </div>
    <NotEnoughStockWarning v-if="form.AccommodationInventoryId.stock.$invalid && model.NumberOfRooms && model.NumberOfRooms > 0" />

    <div v-if="!!gameMetadata.ExperienceInventory.length">
      <div class="input-holder wide has-dropdown">
        <label for="ExperienceInventoryIds">Experiences</label>
        <div class="select-holder">
          <CustomMultiSelect id="ExperienceInventoryIds" name="ExperienceInventoryIds" v-model="model.ExperienceInventoryIds" display="chip"
            option-value="InventoryId" :option-label="formatExperience" :option-disabled="item => item.IsSoldOut"
            :options="gameMetadata.ExperienceInventory" placeholder="Per fixture options" show-clear append-to="self">
            <template #option="{ option }">
              <span>{{ option.DisplayName }}: {{ option.FormattedDateRange }} ({{ formatCurrency(option.UnitRrpExclGst) }} pp) <span
                  v-if="option.IsSoldOut">- SOLD OUT</span></span>
            </template>
          </CustomMultiSelect>
        </div>
        <small class="p-error" v-if="form.ExperienceInventoryIds.$error">{{ form.ExperienceInventoryIds.$errors[0].$message }}</small>
      </div>

      <div v-if="model.Experiences.length">
        <div class="asymmetric-input-group d-flex flex-v-centre no-lines desktop">
          <div class="sub-option"></div>
          <div class="input-holder">Number of People*</div>
        </div>
        <StartBookingExperienceForm v-for="(experience, index) in model.Experiences" :key="experience.ExperienceInventoryId"
          v-model="model.Experiences[index]" :experience-metadata="experienceMetadata[experience.ExperienceInventoryId]" @remove="removeExperience" />
      </div>
    </div>

    <div v-if="!!gameMetadata.TravelExperienceInventory.length">
      <div class="input-holder wide has-dropdown">
        <label for="TravelExperienceInventoryIds">Travel Experiences</label>
        <div class="select-holder">
          <CustomMultiSelect id="TravelExperienceInventoryIds" name="TravelExperienceInventoryIds" v-model="model.TravelExperienceInventoryIds"
            display="chip" option-value="InventoryId" :option-label="formatTravelExperience" :option-disabled="item => item.IsSoldOut"
            :options="gameMetadata.TravelExperienceInventory" placeholder="Per fixture options" show-clear append-to="self">
            <template #option="{ option }">
              <span>{{ option.DisplayName }}: {{ option.FormattedDateRange }} ({{ formatCurrency(option.UnitRrpExclGst) }} pp) <span
                  v-if="option.IsSoldOut">- SOLD OUT</span></span>
            </template>
          </CustomMultiSelect>
        </div>
        <small class="p-error" v-if="form.TravelExperienceInventoryIds.$error">{{ form.TravelExperienceInventoryIds.$errors[0].$message }}</small>
      </div>

      <div v-if="model.TravelExperiences.length">
        <div class="asymmetric-input-group d-flex flex-v-centre extra-items no-lines desktop">
          <div class="sub-option"></div>
          <div class="input-holder">Number of People*</div>
          <div class="input-holder">How many rooms?*</div>
        </div>
        <StartBookingTravelExperienceForm v-for="(experience, index) in model.TravelExperiences" :key="experience.TravelExperienceInventoryId"
          v-model="model.TravelExperiences[index]" :experience-metadata="travelExperienceMetadata[experience.TravelExperienceInventoryId]"
          @remove="removeTravelExperience" />
      </div>
    </div>
    <hr class="white-fade" />
  </div>
</template>
<script lang="ts" setup>
import { StartBookingExperienceRequestModel, StartBookingTravelExperienceRequestModel, type StartBookingGameRequestModel } from '@/components/BookingForm/types';
import type { BookingGameMetadataModel, ExperienceInventoryModel, TravelExperienceInventoryModel } from '@/types/BookingGameMetadataModel';
import useVuelidate from '@vuelidate/core';
import { helpers, maxValue, minValue, required, requiredIf } from '@vuelidate/validators';
import { keyBy } from 'lodash';
import { Select } from 'primevue';
import { computed, watch, type PropType } from 'vue';
import 'vue-json-pretty/lib/styles.css';
import CustomMultiSelect from '../CustomMultiSelect.vue';
import { formatAccommodation, formatCurrency, formatExperience, formatHospitalityTicketInventory, formatTravelExperience } from './formatters';
import NotEnoughStockWarning from './NotEnoughStockWarning.vue';
import StartBookingExperienceForm from './StartBookingExperienceForm.vue';
import StartBookingTravelExperienceForm from './StartBookingTravelExperienceForm.vue';
import { createInventorySoldOutValidator, createInventoryStockValidator } from './validators';

const model = defineModel<StartBookingGameRequestModel>({ required: true });
const props = defineProps({
  index: {
    type: Number,
    required: true,
  },
  gameMetadata: {
    type: Object as PropType<BookingGameMetadataModel>,
    required: true,
  },
  gameCount: {
    type: Number,
    required: true,
  },
  isInternational: {
    type: Boolean,
    required: true,
  },
});

const experienceMetadata = computed<Record<number, ExperienceInventoryModel>>(() => keyBy(props.gameMetadata.ExperienceInventory, 'InventoryId'));
const travelExperienceMetadata = computed<Record<number, TravelExperienceInventoryModel>>(() => keyBy(props.gameMetadata.TravelExperienceInventory, 'InventoryId'));

const minNumberOfRooms = computed(() => {
  // For international packages, min number of rooms should be equal to the number of people divided by 2, rounded up #STH290
  if (!!model.value.AccommodationInventoryId) {
    return props.isInternational ? Math.ceil(model.value.NumberOfPeople / 2) : 1
  }
  return 0;
});

const hospitalityTicketInventory = computed(() => props.gameMetadata.HospitalityTicketInventory.find(x => x.InventoryId == model.value.HospitalityTicketInventoryId));
const isAccommodationRequired = computed(() => props.isInternational ? true : hospitalityTicketInventory.value?.AccommodationRequired ?? false);

watch(hospitalityTicketInventory, (value, oldValue) => {
  if (value?.ForcedQuantity) {
    model.value.NumberOfPeople = value.ForcedQuantity;
  } else if (oldValue?.ForcedQuantity) {
    model.value.NumberOfPeople = undefined!;
  }
});

const hospitalityTicketStockValidator = createInventoryStockValidator(props.gameMetadata.HospitalityTicketInventory, () => (hospitalityTicketInventory.value?.ForcedQuantity ? 1 : model.value.NumberOfPeople));
const hospitalityTicketSoldOutValidator = createInventorySoldOutValidator(props.gameMetadata.HospitalityTicketInventory);

const accommodationStockValidator = createInventoryStockValidator(props.gameMetadata.AccommodationInventory, () => model.value.NumberOfRooms ?? 0);
const accommodationSoldOutValidator = createInventorySoldOutValidator(props.gameMetadata.AccommodationInventory);

const rules = computed<Partial<Record<keyof StartBookingGameRequestModel, unknown>>>(() => ({
  HospitalityTicketInventoryId: {
    required: helpers.withMessage('Required', required),
    stock: helpers.withMessage('Not enough stock', (value: number | null) => hospitalityTicketStockValidator(value)),
    soldOut: helpers.withMessage('Sold out!', hospitalityTicketSoldOutValidator),
  },
  NumberOfPeople: {
    required,
    min: helpers.withMessage('At least one', minValue(1)),
    max: helpers.withMessage('No more than 500', maxValue(500)),
    $autoDirty: true,
  },
  NumberOfRooms: {
    required: helpers.withMessage(
      'Required',
      requiredIf(() => !!model.value.AccommodationInventoryId)
    ),
    min: helpers.withMessage(() => `At least ${minNumberOfRooms.value ?? 1}`, minValue(minNumberOfRooms)),
    max: helpers.withMessage(() => `No more than ${model.value.NumberOfPeople ?? 0}`, maxValue(model.value.NumberOfPeople)),
    $autoDirty: true,
  },
  AccommodationInventoryId: {
    required: helpers.withMessage('Required', requiredIf(isAccommodationRequired)),
    stock: helpers.withMessage('Not enough stock', accommodationStockValidator),
    soldOut: helpers.withMessage('Sold out!', accommodationSoldOutValidator),
  },
  ExperienceInventoryIds: {},
  TravelExperienceInventoryIds: {},
}));
const form = useVuelidate<StartBookingGameRequestModel>(rules, model);

watch(
  () => model.value.ExperienceInventoryIds,
  (inventoryIds: number[] | null) => {
    if (!inventoryIds) {
      inventoryIds = model.value.ExperienceInventoryIds = [];
    }

    inventoryIds.forEach(inventoryId => {
      const experience = model.value.Experiences.find(g => g.ExperienceInventoryId == inventoryId);
      if (!experience) {
        model.value.Experiences.push(
          new StartBookingExperienceRequestModel({
            ExperienceInventoryId: inventoryId,
            NumberOfPeople: model.value.NumberOfPeople,
          })
        );
      }
    });

    model.value.Experiences = model.value.Experiences.filter(e => inventoryIds.includes(e.ExperienceInventoryId));
  },
  { deep: true }
);

function removeExperience(inventoryId: number) {
  const index = model.value.ExperienceInventoryIds.indexOf(inventoryId);
  if (index !== -1) {
    model.value.ExperienceInventoryIds.splice(index, 1);
  }
}

watch(
  () => model.value.TravelExperienceInventoryIds,
  (inventoryIds: number[] | null) => {
    if (!inventoryIds) {
      inventoryIds = model.value.TravelExperienceInventoryIds = [];
    }
    inventoryIds.forEach(inventoryId => {
      const experience = model.value.TravelExperiences.find(g => g.TravelExperienceInventoryId == inventoryId);
      if (!experience) {
        model.value.TravelExperiences.push(
          new StartBookingTravelExperienceRequestModel({
            TravelExperienceInventoryId: inventoryId,
            NumberOfPeople: model.value.NumberOfPeople,
            NumberOfRooms: model.value.NumberOfRooms,
          })
        );
      }
    });

    model.value.TravelExperiences = model.value.TravelExperiences.filter(e => inventoryIds.includes(e.TravelExperienceInventoryId));
  },
  { deep: true }
);

function removeTravelExperience(inventoryId: number) {
  const index = model.value.TravelExperienceInventoryIds.indexOf(inventoryId);
  if (index !== -1) {
    model.value.TravelExperienceInventoryIds.splice(index, 1);
  }
}
</script>
